import { faCamera } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormApi } from 'lib-node-amb-renderer';
import { useState } from 'react';

import Text from '~/common/componentMapper/Text';
import Button from '~/common/components/Button';
import Stack from '~/common/components/Stacks/Stack';
import { SNAPSHOT_BUTTON_LABEL } from '~/common/constants';

import { Icon } from '../Icon';
import {
  StyledAnswerBox,
  StyledComponentBox,
  StyledFooter,
  StyledFormContainerBox,
  StyledIconBox,
  StyledInnerContainerBox,
  StyledLink,
  StyledText,
} from './Styled';
import {
  CONFIDENCE_LEVEL,
  CONFIDENCE_LEVEL_ICON_COLORS,
  TITLE_ICON_TYPE,
} from './constants';

// Confidence helper
const handleConfidenceLevelColorInd = (confidenceLevel) => {
  switch (+confidenceLevel) {
    case CONFIDENCE_LEVEL.GREEN:
      return CONFIDENCE_LEVEL_ICON_COLORS.GREEN;
    case CONFIDENCE_LEVEL.RED:
      return CONFIDENCE_LEVEL_ICON_COLORS.RED;
    case CONFIDENCE_LEVEL.YELLOW:
      return CONFIDENCE_LEVEL_ICON_COLORS.YELLOW;
    default:
      return CONFIDENCE_LEVEL_ICON_COLORS.GREEN;
  }
};

type QuestionBoxIconProps = {
  iconType?: string;
};

const QuestionBoxIcon = ({ iconType }: Readonly<QuestionBoxIconProps>) => {
  return (
    <StyledIconBox width="4%" paddingTop="0">
      <Icon
        sx={{
          display: 'grid',
          placeItems: 'center',
          backgroundColor: 'blue.600',
          color: 'white',
          borderRadius: '.3rem',
          minWidth: '1.5rem',
          minHeight: '1.5rem',
        }}
        iconType={iconType}
      />
    </StyledIconBox>
  );
};

type QuestionBoxConfidenceFooterProps = {
  detailedAnswer?: string;
  confidenceLevel?: number;
};

const QuestionBoxConfidenceFooter = ({
  detailedAnswer,
  confidenceLevel,
}: Readonly<QuestionBoxConfidenceFooterProps>) => {
  const [detailedAnswerStyle, setDetailedAnswerStyle] = useState({
    overflow: false,
    whiteSpace: false,
    showMore: false,
  });

  const { overflow, whiteSpace, showMore } = detailedAnswerStyle;
  const hasEllipsis = (detailedAnswer?.split('')?.length ?? 0) > 50;

  const handleDetailedAnswerStyles = () => {
    setDetailedAnswerStyle((prev) => {
      return {
        ...prev,
        overflow: !prev.overflow,
        whiteSpace: !prev.whiteSpace,
        showMore: !prev.showMore,
      };
    });
  };
  return (
    <StyledFooter>
      <StyledAnswerBox>
        <StyledIconBox paddingTop="0" paddingRight=".5rem" minWidth="5%">
          <Icon
            sx={{ fontSize: 'clamp(1.5rem, 1.5rem, 2rem)' }}
            iconType={TITLE_ICON_TYPE.GAUGE}
            color={handleConfidenceLevelColorInd(confidenceLevel)}
          />
        </StyledIconBox>
        <StyledText overflow={overflow} whiteSpace={whiteSpace}>
          {detailedAnswer}
        </StyledText>
        {hasEllipsis && (
          <StyledLink onClick={handleDetailedAnswerStyles} minWidth={'100px'}>
            {showMore ? 'Show Less' : 'Show More'}
          </StyledLink>
        )}
      </StyledAnswerBox>
    </StyledFooter>
  );
};

type QuestionBoxAnswerFooterProps = {
  answer?: string;
  detailedAnswer?: string;
  confidenceLevel?: number;
};

const QuestionBoxAnswerFooter = ({
  answer,
  detailedAnswer,
  confidenceLevel,
}: Readonly<QuestionBoxAnswerFooterProps>) => {
  const [showBOLTAnswer, setShowBOLTAnswer] = useState(false);
  const handleBOLTAnswerShowHide = () => {
    setShowBOLTAnswer((prev) => !prev);
  };
  return (
    <StyledFooter>
      <Stack direction="column" paddingTop="1rem">
        {showBOLTAnswer && (
          <Stack direction="row">
            <StyledIconBox paddingTop="0" paddingRight=".5rem" minWidth="4%">
              <Icon
                sx={{ fontSize: '1.5rem' }}
                iconType={TITLE_ICON_TYPE.GAUGE}
                color={handleConfidenceLevelColorInd(confidenceLevel)}
              />
            </StyledIconBox>
            <Stack direction="column" width="90%">
              <StyledText>BOLT Answer: {answer}</StyledText>
              <Text
                sx={{
                  fontStyle: 'italic',
                  color: 'gray.600',
                  padding: '0.3rem 0',
                }}
              >
                {detailedAnswer}
              </Text>
            </Stack>
          </Stack>
        )}
        <StyledLink
          minWidth={'30%'}
          textAlign="left"
          placeItems="left"
          onClick={handleBOLTAnswerShowHide}
        >
          {showBOLTAnswer ? 'Hide BOLT Answer' : 'Show BOLT Answer'}
        </StyledLink>
      </Stack>
    </StyledFooter>
  );
};

type QuestionBoxSmartQuestionProps = {
  input?: any;
  detailedAnswer?: string;
  answer?: string;
  confidenceLevel?: number;
  renderForm?: (...args: any[]) => any;
  iconType?: string;
  snippets?: any[];
  handlePDFOnFocus?: (...args: any[]) => any;
  displayFlags?: {
    displayIcon?: boolean;
    displayConfidenceFooter?: boolean;
    displayBOLTAnswerFooter?: boolean;
  };
};

export default function QuestionBoxSmartQuestion({
  input,
  detailedAnswer,
  answer,
  confidenceLevel,
  iconType,
  snippets,
  handlePDFOnFocus,

  displayFlags: {
    displayIcon,
    displayConfidenceFooter,
    displayBOLTAnswerFooter,
  },
}: Readonly<QuestionBoxSmartQuestionProps>) {
  const { renderForm } = useFormApi();
  const hasSnippets = !!snippets.length;

  return (
    <StyledInnerContainerBox>
      <StyledFormContainerBox>
        <StyledComponentBox>
          <StyledFormContainerBox>{renderForm([input])}</StyledFormContainerBox>
        </StyledComponentBox>
        {displayIcon && <QuestionBoxIcon iconType={iconType} />}
      </StyledFormContainerBox>
      {displayConfidenceFooter && (
        <QuestionBoxConfidenceFooter
          confidenceLevel={confidenceLevel}
          detailedAnswer={detailedAnswer}
        />
      )}
      {displayBOLTAnswerFooter && (
        <QuestionBoxAnswerFooter
          answer={answer}
          detailedAnswer={detailedAnswer}
          confidenceLevel={confidenceLevel}
        />
      )}
      {hasSnippets && (
        <Button
          mt="1rem"
          isPrimary={false}
          onClick={() => handlePDFOnFocus()}
          aria-label={SNAPSHOT_BUTTON_LABEL}
        >
          <FontAwesomeIcon icon={faCamera} />
          <Text ml=".5rem" as="span">
            Snapshot
          </Text>
        </Button>
      )}
    </StyledInnerContainerBox>
  );
}
